import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { subscriptionsService } from "../features/subscriptions/service";
import { buildFeatureRows, type PlanFeatureRow } from "../features/subscriptions/planFeatures";
import type { FeatureCatalogEntry, TierLimitsResponse } from "../features/subscriptions/types";
import FeatureInfoTooltip from "../components/common/FeatureInfoTooltip";

// ── Static tier metadata (numbers come live from /tier-limits) ────────────────

const SELLER_TIERS = [
  {
    key: "free",
    label: "Free",
    color: "border-gray-700",
    badge: "bg-gray-800 text-gray-300",
    highlight: false,
    tagline: "Get started — no credit card needed",
  },
  {
    key: "starter",
    label: "Starter",
    color: "border-blue-700",
    badge: "bg-blue-900 text-blue-200",
    highlight: false,
    tagline: "For individual agents and small agencies",
  },
  {
    key: "mid",
    label: "Mid",
    color: "border-indigo-500",
    badge: "bg-indigo-900 text-indigo-200",
    highlight: true,
    tagline: "Best for growing travel companies",
  },
  {
    key: "premium",
    label: "Premium",
    color: "border-purple-600",
    badge: "bg-purple-900 text-purple-200",
    highlight: false,
    tagline: "For large DMCs and hotel chains",
  },
];

const BUYER_TIERS = [
  {
    key: "free",
    label: "Free",
    color: "border-gray-700",
    badge: "bg-gray-800 text-gray-300",
    highlight: false,
    tagline: "Browse and discover travel deals",
  },
  {
    key: "pro",
    label: "Pro",
    color: "border-teal-600",
    badge: "bg-teal-900 text-teal-200",
    highlight: false,
    tagline: "For frequent travel buyers",
  },
  {
    key: "studio",
    label: "Studio",
    color: "border-cyan-500",
    badge: "bg-cyan-900 text-cyan-200",
    highlight: true,
    tagline: "For travel agencies buying at scale",
  },
  {
    key: "elite",
    label: "Elite",
    color: "border-amber-500",
    badge: "bg-amber-900 text-amber-200",
    highlight: false,
    tagline: "For high-volume B2B travel buyers",
  },
];

type TierMeta = (typeof SELLER_TIERS)[0];
type TierWithFeatures = TierMeta & { features: PlanFeatureRow[] };

function TierCard({
  tier,
  onSelect,
  featureCatalog,
}: {
  tier: TierWithFeatures;
  onSelect?: () => void;
  featureCatalog: Record<string, FeatureCatalogEntry>;
}) {
  return (
    <div
      className={`relative flex flex-col bg-gray-900 rounded-2xl border-2 ${tier.color} p-6 ${tier.highlight ? "ring-2 ring-indigo-500/40" : ""}`}
    >
      {tier.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-indigo-600 rounded-full text-xs font-semibold text-white whitespace-nowrap">
          Most Popular
        </div>
      )}
      <div className="mb-4">
        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${tier.badge}`}>
          {tier.label}
        </span>
        <p className="text-gray-400 text-sm mt-2">{tier.tagline}</p>
      </div>
      <ul className="space-y-2.5 flex-1 mb-6">
        {tier.features.map((f) => (
          <li key={f.label} className="flex items-center justify-between text-sm">
            <span className="text-gray-400 flex items-center">
              <span className="mr-2">{f.icon}</span>
              {f.label}
              <FeatureInfoTooltip entry={f.action ? featureCatalog[f.action] : undefined} />
            </span>
            <span
              className={`font-medium ${
                f.dim ? "text-gray-600 line-through" : f.green ? "text-green-400" : "text-white"
              }`}
            >
              {f.value}
            </span>
          </li>
        ))}
      </ul>
      {onSelect && tier.key !== "free" && (
        <button
          onClick={onSelect}
          className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${
            tier.highlight
              ? "bg-indigo-600 hover:bg-indigo-500 text-white"
              : "bg-gray-800 hover:bg-gray-700 text-white border border-gray-700"
          }`}
        >
          Get {tier.label}
        </button>
      )}
    </div>
  );
}

export default function PricingPage() {
  const [tierLimits, setTierLimits] = useState<TierLimitsResponse | null>(null);
  const [featureCatalog, setFeatureCatalog] = useState<Record<string, FeatureCatalogEntry>>({});
  const navigate = useNavigate();
  const { user } = useAuth();
  const loggedInRole = user?.role === "buyer" ? "buyer" : user?.role === "seller" ? "seller" : null;
  const [role, setRole] = useState<"seller" | "buyer">(loggedInRole ?? "seller");

  useEffect(() => {
    if (loggedInRole) setRole(loggedInRole);
  }, [loggedInRole]);

  useEffect(() => {
    let cancelled = false;
    subscriptionsService
      .getTierLimits(role)
      .then((data) => {
        if (!cancelled) setTierLimits(data);
      })
      .catch(() => {
        if (!cancelled) setTierLimits(null);
      });
    return () => {
      cancelled = true;
    };
  }, [role]);

  useEffect(() => {
    let cancelled = false;
    subscriptionsService
      .getFeatureCatalog()
      .then((data) => {
        if (cancelled) return;
        const byAction: Record<string, FeatureCatalogEntry> = {};
        for (const f of data.features) byAction[f.action] = f;
        setFeatureCatalog(byAction);
      })
      .catch(() => {
        if (!cancelled) setFeatureCatalog({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tierMetas = role === "seller" ? SELLER_TIERS : BUYER_TIERS;
  const tiers: TierWithFeatures[] = tierMetas.map((meta) => ({
    ...meta,
    features: buildFeatureRows(role, meta.key, tierLimits?.tiers?.[meta.key]),
  }));

  const handleSelect = () => {
    if (user) {
      navigate("/subscription");
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-3">Pricing Plans</h1>
          <p className="text-gray-400 text-lg">
            Start free, upgrade when you need more. 14-day free trial on all paid plans.
          </p>
        </div>

        {/* Role toggle — only shown to logged-out users */}
        {!loggedInRole && <div className="flex justify-center mb-10">
          <div className="flex bg-gray-900 rounded-xl p-1 gap-1">
            <button
              onClick={() => setRole("seller")}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                role === "seller" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              Seller Plans
            </button>
            <button
              onClick={() => setRole("buyer")}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                role === "buyer" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              Buyer Plans
            </button>
          </div>
        </div>}

        {/* Tier cards */}
        <div className={`grid gap-6 ${tiers.length === 4 ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 md:grid-cols-3"}`}>
          {tiers.map((tier) => (
            <TierCard key={tier.key} tier={tier} onSelect={handleSelect} featureCatalog={featureCatalog} />
          ))}
        </div>

        {/* Feature comparison note */}
        <div className="mt-12 bg-gray-900 rounded-2xl border border-gray-800 p-6">
          <h2 className="text-sm font-semibold text-white mb-4">
            {role === "seller" ? "Seller" : "Buyer"} Plan Comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left text-gray-500 py-2 pr-4 font-medium w-40">Feature</th>
                  {tiers.map((t) => (
                    <th key={t.key} className="text-center py-2 px-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${t.badge}`}>
                        {t.label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {tiers[0].features.map((feature, fi) => (
                  <tr key={feature.label}>
                    <td className="py-2.5 pr-4 text-gray-400 flex items-center gap-1.5">
                      <span>{feature.icon}</span>
                      <span>{feature.label}</span>
                      <FeatureInfoTooltip entry={feature.action ? featureCatalog[feature.action] : undefined} />
                    </td>
                    {tiers.map((t) => (
                      <td key={t.key} className="text-center py-2.5 px-3">
                        <span className={`${
                          t.features[fi].dim
                            ? "text-gray-600"
                            : t.features[fi].green
                            ? "text-green-400"
                            : "text-white"
                        }`}>
                          {t.features[fi].value}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-center text-gray-600 text-sm mt-8">
          All limits reset monthly. Contact support for enterprise pricing.
        </p>
      </div>
    </div>
  );
}
